import { InputField, isTabletPortraitDown } from '@enter/core';
import { mdiArrowLeft } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import { PasswordMeter } from 'password-meter';
import React, { useState } from 'react';
import { handleError } from 'stores';
import * as Yup from 'yup';
import { ISettingsPage } from '../shared';
import * as S from '../shared.styles';

interface IPassword {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const schema = Yup.object().shape({
    currentPassword: Yup.string().required('Password is required'),
    newPassword: Yup.string().required('New password is required'),
    confirmPassword: Yup.string().required('Confirm password is required'),
});
const maxLength = 170;
const meter = new PasswordMeter(
    {
        minLength: 8,
    },
    {
        '80': 'weak',
        '120': 'fair',
        _: 'strong',
    },
);

export const Password: React.FunctionComponent = inject('rootStore')(
    observer(
        (props: ISettingsPage): React.ReactElement => {
            const [error, setError] = useState('');
            const [strength, setStrength] = useState('');

            const rootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const initial: IPassword = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };

            const comparePasswords = (
                currentPassword?: string,
                newPassword?: string,
                confirmPassword?: string,
            ): void => {
                const matching: boolean = !!newPassword && !!confirmPassword && newPassword === confirmPassword;
                setError('');
                if (matching) {
                    if (confirmPassword === currentPassword) {
                        setError('New password must be different from old password');
                    }
                    const results = meter.getResult(newPassword as string);
                    if (_.isEmpty(results.errors)) {
                        setStrength(results.status);
                        return;
                    }
                } else if (!matching && newPassword && confirmPassword) {
                    setError('New password and confirmation do not match');
                }
                setStrength('');
            };

            const validate = ({ currentPassword, newPassword, confirmPassword }: IPassword): void =>
                comparePasswords(currentPassword, newPassword, confirmPassword);

            const handleCancel = (formik: FormikProps<IPassword>): (() => Promise<void>) => {
                return async (): Promise<void> => {
                    await formik.resetForm();
                    comparePasswords();
                };
            };

            const handleSubmit = async (
                { currentPassword, newPassword }: IPassword,
                { resetForm, setSubmitting }: FormikActions<IPassword>,
            ): Promise<void> => {
                setSubmitting(true);
                try {
                    await rootStore.settingsStore.changeUserPassword(currentPassword, newPassword);
                    // NB: trigger re-initialization with changed values
                    _.assign(initial, {
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                    });
                    resetForm();
                    comparePasswords();
                } catch (err) {
                    const payload = await handleError(err);
                    const message = _.get(payload, 'message', 'A server error occurred.');
                    setError(message);
                    throw new Error(message);
                }
                setSubmitting(false);
            };

            return (
                <Formik
                    enableReinitialize={true}
                    initialValues={initial}
                    validationSchema={schema}
                    onSubmit={handleSubmit}
                    validate={validate}>
                    {(formik: FormikProps<IPassword>): React.ReactElement => (
                        <S.SubCategoryItem>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings/security-and-safety">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            <S.SubCategoryH1>What is your password?</S.SubCategoryH1>
                            <S.SubCategoryForm>
                                <S.InputGroup>
                                    <S.Label>Current Password</S.Label>
                                    <S.FormField
                                        type="password"
                                        name="currentPassword"
                                        maxLength={maxLength}
                                        component={InputField}
                                    />
                                </S.InputGroup>
                                <S.InputGroup>
                                    <S.Label>New Password</S.Label>
                                    <S.FormField
                                        type="password"
                                        name="newPassword"
                                        maxLength={maxLength}
                                        component={InputField}
                                    />
                                </S.InputGroup>
                                <S.InputGroup>
                                    <S.Label>Confirm new Password</S.Label>
                                    <S.FormField
                                        type="password"
                                        name="confirmPassword"
                                        maxLength={maxLength}
                                        component={InputField}
                                    />
                                </S.InputGroup>
                                {strength && (
                                    <S.PasswordStrength strength={strength}>
                                        Password strength: {strength}
                                    </S.PasswordStrength>
                                )}
                                {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
                            </S.SubCategoryForm>
                            <S.ButtonGroup
                                useNotify={true}
                                onCancel={handleCancel(formik)}
                                onSave={formik.submitForm}
                                changed={formik.dirty}
                            />
                        </S.SubCategoryItem>
                    )}
                </Formik>
            );
        },
    ),
);
